import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { logger } from "../utils/logger";
import { runPool } from "../utils/batchUpload";
import {
  parseFolder,
  parseStudy,
  buildMultipartBody,
  makeBoundary,
  multipartContentType,
  DEFAULT_STRIP_PATIENT_INFO,
  StudyValidationError,
  type FolderStudy,
} from "../utils/dicomUpload";
import { useReaderCohort, type ReaderStudyItem } from "./useStudyQueries";
import type { PhiFinding, StudyValidation } from "../components/upload/types";
import { UPLOAD_BATCH_MB, UPLOAD_MAX_MB, UPLOAD_SINGLE_REQUEST_MB } from "../config/uploadLimits";
import { uploadReceiptCohortId } from "../utils/uploadReceipt";
import {
  DirectUploadError,
  isDirectUploadCancelled,
  uploadStudyDirect,
} from "../utils/directStudyUpload";

export type BatchPhase = "idle" | "parsing" | "review" | "uploading" | "analyzing" | "done";
export type BatchCaseStatus =
  | "pending"
  | "uploading"
  | "uploaded"
  | "upload-failed"
  | "analyzing"
  | "ready"
  | "failed";

export interface BatchCase {
  studyInstanceUid: string;
  validation: StudyValidation;
  phiFindings: PhiFinding[];
  status: BatchCaseStatus;
  progress: number; // 0..100 upload progress
  uploadedAt?: number; // Date.now() at successful POST (drives the analyze timeout)
  message?: string; // failure reason
  viewerId?: string; // study_uid::job_id once the cohort row lands
  retriedAt?: number; // Date.now() at a retry — the barrier ignores any cohort row older than this
}

export interface UseBatchUpload {
  phase: BatchPhase;
  cases: BatchCase[];
  excludedStudies: { studyInstanceUid: string; reason: string }[];
  skippedFiles: { reason: string; count: number }[];
  aggregatePhi: { tag: string; studyCount: number }[];
  strip: boolean;
  setStrip: (v: boolean) => void;
  acknowledged: boolean;
  setAcknowledged: (v: boolean) => void;
  counts: { total: number; ready: number; failed: number; terminal: number };
  begin: (files: File[]) => void;
  startUpload: () => void;
  retryCase: (studyInstanceUid: string) => void;
  reset: () => void;
}

export const BATCH_UPLOAD_CONCURRENCY = 4;
export const ANALYZE_TIMEOUT_MS = 10 * 60 * 1000;
// A whole-study upload legitimately runs for minutes; match the single-study
// path's ceiling (UploadStudyPage) so a slow upload isn't false-failed.
const UPLOAD_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const MIB = 1024 * 1024;
const LARGE_STUDY_THRESHOLD_BYTES = UPLOAD_SINGLE_REQUEST_MB * MIB;
const LARGE_STUDY_BATCH_BYTES = UPLOAD_BATCH_MB * MIB;
const LARGE_STUDY_MAX_REQUEST_BYTES = 300 * MIB;
const LARGE_STUDY_BATCH_RETRIES = 2;
const LARGE_STUDY_MAX_BATCHES = 40;

/** Greedily group complete DICOM files; an instance is never split. */
export function partitionStudyFiles(files: File[], targetBytes: number): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;
  for (const file of files) {
    if (current.length && currentBytes + file.size > targetBytes) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }
  if (current.length) batches.push(current);
  return batches;
}

function makeUploadId(): string {
  return globalThis.crypto?.randomUUID?.() ?? makeBoundary();
}

async function retryBatch<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= LARGE_STUDY_BATCH_RETRIES; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (isDirectUploadCancelled(error) || attempt === LARGE_STUDY_BATCH_RETRIES) throw error;
      await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
    }
  }
  throw lastError;
}

// `upload-failed` is terminal too — the done-barrier and BatchAnalyzePanel both
// treat it that way, so `counts.terminal` (the "ANALYZING · N/M" subtitle) must
// match or it under-counts and the header disagrees with the panel.
const TERMINAL: BatchCaseStatus[] = ["ready", "failed", "upload-failed"];

/** Epoch ms of a cohort row's job creation, or 0 when absent/unparseable. Used
 *  to tell a stale pre-retry job from the reprocessed one (a re-upload mints a
 *  new job — `job_id` is the device-server table's primary key — so the fresh
 *  row carries a newer `created_at`). */
function rowTime(row: ReaderStudyItem): number {
  const t = Date.parse(row.created_at);
  return Number.isNaN(t) ? 0 : t;
}

/** Per-case upload failure copy — surfaces the real cause (mirrors the
 *  single-study path). A 5xx is a server-side problem, not the user's study,
 *  so we don't imply a plain retry will fix it. */
function uploadFailureMessage(err: unknown): string {
  if (err instanceof DirectUploadError) {
    if (err.status === 413) return "Study is over the upload size limit.";
    if (err.status === 401) return "Upload authorization expired - retry this study.";
    if (err.status != null && err.status >= 500)
      return `Upload service error (${err.status}) - the server couldn't accept this study. Not your study; try again later.`;
    if (err.status != null) return `Upload rejected (${err.status}) - retry this study.`;
  }
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 413) return "Study is over the upload size limit.";
    if (status === 401) return "Session expired — sign in again, then retry.";
    if (status != null && status >= 500)
      return `Upload service error (${status}) — the server couldn't accept this study. Not your study; try again later.`;
    if (status != null) return `Upload rejected (${status}) — retry this study.`;
  }
  return "Upload failed — retry this study.";
}

/**
 * Folder upload state machine. `shared` is deliberately a boolean, rather than
 * a caller-supplied cohort id: the backend derives the company cohort from the
 * authenticated user, preserving the same tenant boundary as single uploads.
 */
export function useBatchUpload(
  token: string | null,
  cohortId: string | null,
  shared = false,
  ensureReceiptCohortAccess?: (cohortId: string) => Promise<string>,
): UseBatchUpload {
  const [phase, setPhase] = useState<BatchPhase>("idle");
  const [cases, setCases] = useState<BatchCase[]>([]);
  const [excludedStudies, setExcluded] = useState<{ studyInstanceUid: string; reason: string }[]>(
    [],
  );
  const [skippedFiles, setSkipped] = useState<{ reason: string; count: number }[]>([]);
  const [strip, setStrip] = useState(DEFAULT_STRIP_PATIENT_INFO);
  const [acknowledged, setAcknowledged] = useState(false);
  // Set from the proxy receipt, whose cohort_id reflects the current profile
  // even when the JWT's company_id claim is stale after a company reassignment.
  const [receiptCohortId, setReceiptCohortId] = useState<string | null>(null);
  const [receiptToken, setReceiptToken] = useState<string | null>(null);

  // Keep only File references during review. Decode each study immediately
  // before its upload so large batches don't retain every instance in memory.
  const [staged, setStaged] = useState<FolderStudy[]>([]);

  // When any case last reached a terminal cohort status — the stall-detector
  // floor for the analyze timeout (see the barrier effect below). A ref, not
  // state: it must update inside the setCases reducer without re-triggering it.
  const lastProgressAtRef = useRef(0);

  // The analyze-barrier poll follows the selected private or company cohort
  // while analyzing (pollWhilePending=true), so it survives the initial 404
  // before reader-ingest lands the first row.
  const { data: cohort } = useReaderCohort(
    receiptToken ?? token,
    receiptCohortId ?? cohortId,
    phase === "analyzing",
  );

  const begin = useCallback((files: File[]) => {
    setPhase("parsing");
    // Starting another batch replaces the result retained for the prior one.
    // Clear it synchronously so no stale studies flash while parseFolder runs.
    setCases([]);
    setStaged([]);
    setExcluded([]);
    setSkipped([]);
    setAcknowledged(false);
    setStrip(DEFAULT_STRIP_PATIENT_INFO);
    setReceiptCohortId(null);
    setReceiptToken(null);
    lastProgressAtRef.current = 0;
    void (async () => {
      try {
        const result = await parseFolder(files, UPLOAD_MAX_MB * 1024 * 1024);
        setExcluded(result.excludedStudies);
        setSkipped(result.skippedFiles);
        setStaged(result.studies);
        setCases(
          result.studies.map((s) => ({
            studyInstanceUid: s.studyInstanceUid,
            validation: s.validation,
            phiFindings: s.phiFindings,
            status: "pending" as BatchCaseStatus,
            progress: 0,
          })),
        );
        setPhase(result.studies.length > 0 ? "review" : "idle");
      } catch (err) {
        logger.error("Live Upload (batch): parseFolder failed", err);
        setExcluded([]);
        setSkipped([{ reason: "couldn't read the selected folder", count: files.length }]);
        setStaged([]);
        setCases([]);
        setPhase("idle");
      }
    })();
  }, []);

  const reset = useCallback(() => {
    setPhase("idle");
    setCases([]);
    setStaged([]);
    lastProgressAtRef.current = 0;
    setExcluded([]);
    setSkipped([]);
    setStrip(DEFAULT_STRIP_PATIENT_INFO);
    setAcknowledged(false);
    setReceiptCohortId(null);
    setReceiptToken(null);
  }, []);

  const patchCase = useCallback((uid: string, patch: Partial<BatchCase>) => {
    setCases((prev) => prev.map((c) => (c.studyInstanceUid === uid ? { ...c, ...patch } : c)));
  }, []);

  const uploadOne = useCallback(
    async (study: FolderStudy) => {
      const uid = study.studyInstanceUid;
      // Clear the prior attempt's `uploadedAt` up front so a retry restarts its
      // analyze deadline rather than inheriting a stale (already-expired) stamp.
      patchCase(uid, {
        status: "uploading",
        progress: 0,
        message: undefined,
        uploadedAt: undefined,
      });
      try {
        const studyBytes =
          study.validation.totalBytes ?? study.files.reduce((sum, file) => sum + file.size, 0);
        let receipt: unknown;
        if (studyBytes <= LARGE_STUDY_THRESHOLD_BYTES) {
          // Preserve the established path byte-for-byte for ordinary studies.
          const parsedStudy = await parseStudy(study.files);
          const boundary = makeBoundary();
          const body = buildMultipartBody(parsedStudy.instances, boundary, strip);
          receipt = await uploadStudyDirect({
            studyUid: uid,
            shared,
            body,
            contentType: multipartContentType(boundary),
            timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS,
            onProgress: (loaded, total) => {
              if (total) patchCase(uid, { progress: Math.round((loaded / total) * 100) });
            },
          });
        } else {
          // This function is itself one runPool worker. Looping here means one
          // large study occupies one of the existing four slots while its
          // requests remain sequential and independently retryable.
          const fileBatches = partitionStudyFiles(study.files, LARGE_STUDY_BATCH_BYTES);
          if (fileBatches.length > LARGE_STUDY_MAX_BATCHES) {
            throw new StudyValidationError(
              "This study contains instances too large to fit within the 8 GB upload profile.",
            );
          }
          const uploadId = makeUploadId();
          let resumeToken: string | undefined;
          let completedBytes = 0;
          for (let index = 0; index < fileBatches.length; index += 1) {
            const batchFiles = fileBatches[index];
            const batchBytes = batchFiles.reduce((sum, file) => sum + file.size, 0);
            if (batchBytes > LARGE_STUDY_MAX_REQUEST_BYTES) {
              throw new StudyValidationError(
                "A single DICOM instance is larger than the large-study batch limit.",
              );
            }
            const parsedBatch = await parseStudy(batchFiles);
            const boundary = makeBoundary();
            const body = buildMultipartBody(parsedBatch.instances, boundary, strip);
            receipt = await retryBatch(() =>
              uploadStudyDirect({
                studyUid: uid,
                shared,
                body,
                contentType: multipartContentType(boundary),
                timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS,
                // retryBatch owns fresh-ticket retries for this resumable chunk.
                maxAttempts: 1,
                batch: { uploadId, index, count: fileBatches.length, resumeToken },
                onResumeToken: (token) => {
                  resumeToken = token;
                },
                onProgress: (loaded, total) => {
                  if (!total) return;
                  const sent = completedBytes + (loaded / total) * batchBytes;
                  patchCase(uid, {
                    progress: Math.min(99, Math.round((sent / studyBytes) * 100)),
                  });
                },
              }),
            );
            completedBytes += batchBytes;
          }
        }
        const receiptCohortId = uploadReceiptCohortId(receipt);
        // Do not start polling a reassigned company cohort with the prior JWT:
        // reader authorization keys company-upload-* on the token company_id.
        const pollingToken = ensureReceiptCohortAccess
          ? await ensureReceiptCohortAccess(receiptCohortId)
          : token;
        setReceiptToken(pollingToken);
        setReceiptCohortId(receiptCohortId);
        patchCase(uid, { status: "uploaded", progress: 100, uploadedAt: Date.now() });
      } catch (err) {
        logger.error(`Live Upload (batch): POST failed for ${uid}`, err);
        patchCase(uid, { status: "upload-failed", message: uploadFailureMessage(err) });
        throw err; // surfaces as a rejected settled result; pool continues
      }
    },
    [ensureReceiptCohortAccess, patchCase, shared, strip, token],
  );

  const startUpload = useCallback(() => {
    if (!acknowledged || staged.length === 0) return;
    setPhase("uploading");
    void (async () => {
      await runPool(staged, BATCH_UPLOAD_CONCURRENCY, uploadOne);
      // Every case is now uploaded or upload-failed — cross the barrier into
      // analyze. Start the stall window here, not when the first study finished
      // uploading: a large bounded-concurrency batch can spend longer than the
      // analysis timeout uploading its tail, while the cohort query still holds
      // a pre-batch snapshot. Without this floor, early successful uploads are
      // failed before the first fresh analyze-phase poll can observe them.
      // Cases that never uploaded stay terminal-failed there.
      lastProgressAtRef.current = Date.now();
      setPhase("analyzing");
    })();
  }, [acknowledged, staged, uploadOne]);

  const retryCase = useCallback(
    (uid: string) => {
      const study = staged.find((s) => s.studyInstanceUid === uid);
      if (!study) return;
      // Stamp the retry and drop the prior attempt's result up front: `retriedAt`
      // makes the barrier ignore the stale pre-retry cohort row until the
      // reprocessed job lands, and moving off the terminal status immediately
      // (before uploadOne's async patch) keeps the done-barrier from flipping the
      // batch back to `done` in the render gap.
      patchCase(uid, { status: "uploading", retriedAt: Date.now(), viewerId: undefined });
      setPhase("analyzing");
      void uploadOne(study).catch((err) => {
        // uploadOne already logged + marked the case upload-failed; log the
        // swallowed settled rejection so the retry attempt is traceable.
        logger.warn(`Live Upload (batch): retry of ${uid} failed`, err);
      });
    },
    [staged, patchCase, uploadOne],
  );

  // Heartbeat while analyzing so the per-case timeout is re-evaluated even when
  // the cohort poll returns nothing new. Cleared when not analyzing.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (phase !== "analyzing") return;
    const id = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, [phase]);

  // Match uploaded cases against the polled cohort; mark ready/failed; time out
  // a case whose pipeline has stalled. Re-runs on each cohort poll AND each
  // heartbeat tick.
  //
  // The timeout is a STALL detector, not an absolute per-case deadline: job rows
  // sit `pending` from creation until terminal (nothing ever stamps
  // `processing`), so a case's queue position is unobservable and a deep-but-
  // healthy queue would blow any fixed window measured from upload. Instead,
  // every case reaching a terminal cohort status counts as batch progress and
  // restarts the window for the cases still waiting. A case fails as timed out
  // only after ANALYZE_TIMEOUT_MS with no progress anywhere in the batch —
  // whether it never landed a row OR landed one that then stalled non-terminal
  // (hung/queued job container that dies without writing
  // completed/failed/cancelled). For a single-study batch this reduces to the
  // old per-case deadline.
  useEffect(() => {
    if (phase !== "analyzing") return;
    // Newest row per study_uid: a retried study can have BOTH its stale
    // pre-retry job row and the fresh reprocessing row in the cohort (job_id is
    // the PK, so a re-upload adds a new job under the same study_uid). Keep the
    // most recent so the retry guard below compares against the freshest.
    const byUid = new Map<string, ReaderStudyItem>();
    for (const s of cohort?.studies ?? []) {
      const prev = byUid.get(s.study_uid);
      if (!prev || rowTime(s) >= rowTime(prev)) byUid.set(s.study_uid, s);
    }
    const now = Date.now();
    setCases((prev) => {
      let changed = false;
      let progressed = false;
      // Pass 1 — apply fresh cohort rows. Rows land BEFORE the stall check so a
      // case whose result arrives late still shows its real outcome, never a
      // stale "timed out" verdict.
      //
      // "Waiting" excludes terminal cases and mid-(re)upload ones: skipping
      // `uploading` keeps a retry's in-flight re-POST from being judged against
      // the cohort before the fresh job even exists.
      const waiting = (c: BatchCase) => !TERMINAL.includes(c.status) && c.status !== "uploading";
      const afterRows = prev.map((c) => {
        if (!waiting(c)) return c;
        const row = byUid.get(c.studyInstanceUid);
        // For a retried case, ignore a row that predates the retry — it's the
        // stale pre-retry job (its cached `failed`/`completed` would otherwise
        // instantly re-resolve the retry against last attempt's result). The
        // reprocessed job arrives as a newer row (rowTime >= retriedAt).
        const rowIsFresh = row && (c.retriedAt == null || rowTime(row) >= c.retriedAt);
        if (row && rowIsFresh) {
          const viewerId = row.job_id ? `${row.study_uid}::${row.job_id}` : row.study_uid;
          if (row.status === "completed") {
            changed = true;
            progressed = true;
            return { ...c, status: "ready" as BatchCaseStatus, viewerId };
          }
          if (row.status === "failed" || row.status === "cancelled") {
            changed = true;
            progressed = true;
            return {
              ...c,
              status: "failed" as BatchCaseStatus,
              viewerId,
              message: "Analysis failed.",
            };
          }
        }
        // No terminal row yet — keep waiting; nudge an uploaded case into
        // `analyzing` so the UI reflects that ingest is in progress.
        if (c.status === "uploaded") {
          changed = true;
          return { ...c, status: "analyzing" as BatchCaseStatus };
        }
        return c;
      });
      if (progressed) lastProgressAtRef.current = now;
      // Pass 2 — stall check for whatever is still waiting. The window is
      // measured from the latest of the case's own upload (or retry re-stamp)
      // and the batch's last progress.
      const stallFloor = lastProgressAtRef.current;
      const nextCases = afterRows.map((c) => {
        if (!waiting(c)) return c;
        if (c.uploadedAt && now - Math.max(c.uploadedAt, stallFloor) > ANALYZE_TIMEOUT_MS) {
          changed = true;
          return { ...c, status: "failed" as BatchCaseStatus, message: "Analysis timed out." };
        }
        return c;
      });
      return changed ? nextCases : prev;
    });
  }, [phase, cohort, tick]);

  // Final barrier: advance to done once every case is terminal.
  useEffect(() => {
    if (phase !== "analyzing" || cases.length === 0) return;
    const allTerminal = cases.every(
      (c) => c.status === "ready" || c.status === "failed" || c.status === "upload-failed",
    );
    if (allTerminal) setPhase("done");
  }, [phase, cases]);

  const aggregatePhi = useMemo(() => {
    const byTag = new Map<string, number>();
    for (const c of cases) {
      const seen = new Set<string>();
      for (const f of c.phiFindings) {
        if (seen.has(f.tag)) continue;
        seen.add(f.tag);
        byTag.set(f.tag, (byTag.get(f.tag) ?? 0) + 1);
      }
    }
    return Array.from(byTag, ([tag, studyCount]) => ({ tag, studyCount }));
  }, [cases]);

  const counts = useMemo(() => {
    const ready = cases.filter((c) => c.status === "ready").length;
    const failed = cases.filter((c) => c.status === "failed").length;
    const terminal = cases.filter((c) => TERMINAL.includes(c.status)).length;
    return { total: cases.length, ready, failed, terminal };
  }, [cases]);

  return {
    phase,
    cases,
    excludedStudies,
    skippedFiles,
    aggregatePhi,
    strip,
    setStrip,
    acknowledged,
    setAcknowledged,
    counts,
    begin,
    startUpload,
    retryCase,
    reset,
  };
}
